/**
 * Turning whatever an admin drops on the uploader into something a roster can carry.
 *
 * The originals are phone photos — a few megabytes each, arbitrary aspect ratios. A pickup
 * roster is read on a phone, on school wifi, with sixty rows on screen, so what gets stored
 * is a small square WebP. At 512px that is 40-60 KB — a few megabytes for a full roster on
 * first load, cached thereafter.
 *
 * 512 rather than 256 because the headshot is enlargeable: a teacher taps a face to confirm
 * it, and on a retina phone a 256px source rendered across a 300-odd CSS-pixel square is
 * visibly soft at exactly the moment someone is trying to tell two children apart. The row
 * thumbnails do not care either way; the enlarged view does.
 */

/** Stored edge length, in pixels. Sized for the enlarged view, not the row thumbnail. */
export const HEADSHOT_SIZE = 512;

/** WebP quality. 0.85 is visually clean on faces at this size without doubling the bytes. */
const HEADSHOT_QUALITY = 0.85;

export interface CropRect {
  sx: number;
  sy: number;
  size: number;
}

/**
 * The largest centred square that fits inside the source image.
 *
 * Centre rather than top: these are headshots, and a face sits in the middle of the frame far
 * more reliably than it sits at the top. Pure and exported so the geometry is testable
 * without a canvas.
 */
export function centerCropRect(width: number, height: number): CropRect {
  const size = Math.min(width, height);
  return {
    sx: Math.round((width - size) / 2),
    sy: Math.round((height - size) / 2),
    size,
  };
}

/**
 * Decode, centre-crop to a square, downscale and re-encode as WebP.
 *
 * Runs entirely in the browser: the original never leaves the admin's machine, so the bucket
 * only ever holds the small derived image.
 */
export async function toSquareHeadshot(
  file: File,
  size: number = HEADSHOT_SIZE,
): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  try {
    const { sx, sy, size: srcSize } = centerCropRect(bitmap.width, bitmap.height);

    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare an image canvas.');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, sx, sy, srcSize, srcSize, 0, 0, size, size);

    const blob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(resolve, 'image/webp', HEADSHOT_QUALITY),
    );
    if (!blob) throw new Error('Could not encode the image.');
    return blob;
  } finally {
    bitmap.close();
  }
}

/**
 * Where a student's headshot is stored.
 *
 * The random segment means replacing a photo writes a NEW object rather than overwriting the
 * old path. Nothing already handed out — a signed URL, a browser cache entry, a CDN copy —
 * can then answer with the previous child's picture, which on a pickup board is the one
 * failure mode worth spending a few bytes to rule out.
 */
export function headshotObjectPath(studentId: string): string {
  return `${studentId}/${crypto.randomUUID()}.webp`;
}

/**
 * Where an authorized pickup contact's photo is stored.
 *
 * Same bucket, and the same student folder, on purpose. The bucket's read policy resolves the
 * owning student from the FIRST path segment, so filing a pickup contact's face under the
 * child's folder is what makes it readable by exactly the staff who can already see that
 * child — and by nobody else. Keyed by student rather than by contact for the same reason: a
 * contact id in the leading segment would resolve to no student at all and the photo would be
 * unreadable by everyone.
 *
 * The `pickups/` segment only separates the two kinds for a human reading the bucket. The
 * random filename does the same work as it does for headshots: replacing a photo writes a new
 * object, so nothing already handed out can answer with the previous person's face.
 */
export function pickupPhotoObjectPath(studentId: string): string {
  return `${studentId}/pickups/${crypto.randomUUID()}.webp`;
}

/** The bucket headshots live in. Private; read only through signed URLs. */
export const STUDENT_PHOTO_BUCKET = 'student-photos';
